import React, { useEffect } from "react";
import Stack from "@mui/material/Stack";
import LoadingButton from "@mui/lab/LoadingButton";
import { FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import {
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import JumboAppTextField from "@jumbo/components/JumboAppTextField";
import { getUserList } from "app/redux/slice/userSlice";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

const phoneSchema = Yup.string()
  .trim()
  .required("Please enter phone number")
  .matches(/^[0-9]+$/, "Phone number must contain only digits")
  .length(10, "Phone number must be exactly 10 digits");

const validationSchema = Yup.object({
  name: Yup.string().trim().required("Please enter party name"),
  phoneNumbers: Yup.array()
    .of(phoneSchema)
    .min(1, "At least one phone number is required"),
  city: Yup.string().trim(),
  belongsTo: Yup.object().nullable().required("Please select belongs to user"),
});

const UpdatePartyNumber = ({ value, onClose, handleSubmit }) => {
  const dispatch = useDispatch();
  const mobileView = useMediaQuery("(max-width:575px)");
  const userData = useSelector((state) => state?.user);

  useEffect(() => {
    dispatch(getUserList({ sort: 1, field: "name" }));
  }, [dispatch]);

  return (
    <>
      <Formik
        initialValues={{
          name: value?.name ?? "",
          phoneNumbers:
            value?.phoneNumbers?.length > 0 ? value.phoneNumbers : [""],
          city: value?.city ?? "",
          belongsTo: value?.belongsToUser || null,
        }}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={(data, { setSubmitting }) => {
          setSubmitting(true);
          setTimeout(() => {
            handleSubmit(data);
            setSubmitting(false);
          }, 1500);
        }}
      >
        {({ isSubmitting, values, setFieldValue, errors }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField fullWidth name="name" label="Party Name*" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField fullWidth name="city" label="City" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAutoComplete
                  options={userData?.list || []}
                  label="Belongs To*"
                  size="large"
                  getOptionLabel={(option) =>
                    option?.name || option?.email || ""
                  }
                  name="belongsTo"
                  value={values.belongsTo}
                  onChange={(_evt, value) => setFieldValue("belongsTo", value)}
                  helperText={errors?.belongsTo}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FieldArray name="phoneNumbers">
                  {({ push, remove }) => (
                    <>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <Typography variant="h5" mb={0}>
                          Phone Numbers*
                        </Typography>
                        <IconButton
                          color="primary"
                          onClick={() => push("")}
                          size="small"
                        >
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </Stack>
                      <Grid container spacing={2}>
                        {values.phoneNumbers.map((phone, index) => (
                          <Grid item xs={12} sm={6} key={index}>
                            <Stack direction="row" alignItems="flex-start" spacing={1}>
                              <TextField
                                sx={{ flex: 1 }}
                                label={`Phone Number ${index + 1}*`}
                                value={phone}
                                onChange={(e) =>
                                  setFieldValue(
                                    `phoneNumbers[${index}]`,
                                    e.target.value
                                  )
                                }
                                error={
                                  !!(
                                    errors.phoneNumbers &&
                                    errors.phoneNumbers[index]
                                  )
                                }
                                helperText={
                                  errors.phoneNumbers &&
                                  errors.phoneNumbers[index]
                                }
                                inputProps={{ maxLength: 10 }}
                              />
                              {values.phoneNumbers.length > 1 && (
                                <IconButton
                                  color="error"
                                  onClick={() => remove(index)}
                                  size="small"
                                  sx={{ mt: 1 }}
                                >
                                  <RemoveCircleOutlineIcon />
                                </IconButton>
                              )}
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                </FieldArray>
              </Grid>
            </Grid>
            <Stack
              spacing={{ xs: 1, sm: 2 }}
              mt={3}
              direction="row"
              justifyContent="flex-end"
            >
              <LoadingButton
                variant="outlined"
                color="secondary"
                onClick={onClose}
                size="large"
                fullWidth={mobileView}
              >
                Cancel
              </LoadingButton>
              <LoadingButton
                type="submit"
                variant="contained"
                size="large"
                loading={isSubmitting}
                loadingIndicator={<CircularProgress size={20} />}
                fullWidth={mobileView}
              >
                Save
              </LoadingButton>
            </Stack>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default UpdatePartyNumber;
